export const generateWhatsAppOrderMessage = ({
  orderId,
  customerName,
  phone,
  city,
  address,
  notes,
  cartItems,
  subtotal,
  deliveryFee,
  totalAmount,
  paymentMethod,
  paymentAccount,
  transactionRef,
  hasPaymentProof,
  storeSettings,
  lang = 'ur'
}) => {
  let message = '';

  const advanceRequired = storeSettings?.codAdvanceRequired ?? true;
  const advanceAmount = storeSettings?.codAdvanceAmount || 500;
  const remainingCod = Math.max(0, totalAmount - advanceAmount);

  if (lang === 'ur') {
    message = `السلام علیکم ورحمۃ اللہ! 🌿\n\n`;
    message += `میں *${storeSettings.storeNameUrdu || 'ہمالین پنسار سٹور'}* (${storeSettings.dawakhanaNameUrdu || 'دواخانہ طب پاکستانی'}) سے درج ذیل آرڈر کنفرم کرنا چاہتا ہوں:\n\n`;
    message += `📋 *آرڈر نمبر:* #${orderId}\n`;
    message += `👤 *خریدار کا نام:* ${customerName}\n`;
    message += `📞 *رابطہ نمبر:* ${phone}\n`;
    message += `📍 *شہر:* ${city}\n`;
    message += `🏠 *ترسیل کا پتہ:* ${address}\n`;
    if (notes) {
      message += `📝 *اضافی نوٹ / ہدایات:* ${notes}\n`;
    }
    message += `\n🛒 *خریدی گئی اشیاء:* \n`;

    cartItems.forEach((item, index) => {
      const variantName = item.variant?.weight || '';
      message += `${index + 1}. *${item.nameUrdu}* (${variantName})\n`;
      message += `   مقدار: ${item.quantity} × Rs. ${item.unitPrice} = Rs. ${item.quantity * item.unitPrice}\n`;
    });

    message += `\n───────────────\n`;
    message += `💵 *اشیاء کی رقم:* Rs. ${subtotal.toLocaleString()}\n`;
    message += `🚚 *ڈلیوری چارجز:* ${deliveryFee === 0 ? 'مفت (FREE)' : `Rs. ${deliveryFee}`}\n`;
    message += `💰 *کل بل:* *Rs. ${totalAmount.toLocaleString()}*\n`;
    
    if (paymentMethod === 'cod') {
      message += `💳 *طریقہ ادائیگی:* کیش آن ڈلیوری (COD)\n`;
      if (advanceRequired) {
        message += `⚠️ *ایڈوانس تصدیقی رقم:* Rs. ${advanceAmount.toLocaleString()} (ڈلیوری کنفرمیشن)\n`;
        message += `📦 *پارسل ملنے پر بقیہ ادائیگی:* *Rs. ${remainingCod.toLocaleString()}*\n`;
      }
    } else {
      message += `💳 *طریقہ ادائیگی:* مکمل آن لائن ادائیگی (پیشگی)\n`;
    }

    if (paymentAccount) {
      const accountName = paymentAccount === 'bank' ? 'یو بی ایل بینک / ڈیجیٹل کیو آر (UBL Bank / QR)' : 'ایزی پیسہ (EasyPaisa)';
      message += `🏦 *ادائیگی کا ذریعہ / اکاؤنٹ:* ${accountName}\n`;
    }

    if (transactionRef) {
      message += `🔖 *ٹرانزیکشن آئی ڈی (TID):* ${transactionRef}\n`;
    }
    if (hasPaymentProof) {
      message += `📸 *ادائیگی کی رسید:* ادائیگی کا اسکرین شاٹ سسٹم میں اپلوڈ کر دیا گیا ہے۔\n`;
    }
    message += `───────────────\n\n`;
    message += `برائے مہربانی اس آرڈر کی تصدیق فرما دیں اور پارسل روانگی کی تفصیلات ارسال کریں۔ جزاک اللہ خیراً!`;
  } else {
    message = `Assalam-o-Alaikum! 🌿\n\n`;
    message += `I would like to place an order with *${storeSettings.storeNameEn || 'Himalayan Pansar Store'}* (${storeSettings.dawakhanaNameEn || 'Dawakhana Tib-e-Pakistani'}):\n\n`;
    message += `📋 *Order ID:* #${orderId}\n`;
    message += `👤 *Customer Name:* ${customerName}\n`;
    message += `📞 *Phone:* ${phone}\n`;
    message += `📍 *City:* ${city}\n`;
    message += `🏠 *Shipping Address:* ${address}\n`;
    if (notes) {
      message += `📝 *Notes:* ${notes}\n`;
    }
    message += `\n🛒 *Ordered Items:* \n`;

    cartItems.forEach((item, index) => {
      const variantName = item.variant?.weightEn || item.variant?.weight || '';
      message += `${index + 1}. *${item.nameEn || item.nameUrdu}* (${variantName})\n`;
      message += `   Qty: ${item.quantity} × Rs. ${item.unitPrice} = Rs. ${item.quantity * item.unitPrice}\n`;
    });

    message += `\n───────────────\n`;
    message += `💵 *Subtotal:* Rs. ${subtotal.toLocaleString()}\n`;
    message += `🚚 *Delivery Fee:* ${deliveryFee === 0 ? 'FREE' : `Rs. ${deliveryFee}`}\n`;
    message += `💰 *Total Bill:* *Rs. ${totalAmount.toLocaleString()}*\n`;
    
    if (paymentMethod === 'cod') {
      message += `💳 *Payment Method:* Cash on Delivery (COD)\n`;
      if (advanceRequired) {
        message += `⚠️ *Advance Confirmation Deposit:* Rs. ${advanceAmount.toLocaleString()}\n`;
        message += `📦 *Balance Payable Upon Delivery:* *Rs. ${remainingCod.toLocaleString()}*\n`;
      }
    } else {
      message += `💳 *Payment Method:* Full Online Payment (Advance)\n`;
    }

    if (paymentAccount) {
      const accountName = paymentAccount === 'bank' ? 'UBL Bank / Digital QR' : 'EasyPaisa';
      message += `🏦 *Payment Channel:* ${accountName}\n`;
    }

    if (transactionRef) {
      message += `🔖 *Transaction ID (TID):* ${transactionRef}\n`;
    }
    if (hasPaymentProof) {
      message += `📸 *Payment Receipt:* Proof screenshot uploaded in system.\n`;
    }
    message += `───────────────\n\n`;
    message += `Please confirm my order and share dispatch details. Thank you!`;
  }

  return message;
};

export const generateWhatsAppOrderUrl = (params) => {
  const waNumber = params?.storeSettings?.whatsappNumber || '923470005578';
  const cleanNumber = waNumber.replace(/[^0-9]/g, '');
  const message = generateWhatsAppOrderMessage(params);
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
};

export const generateProductInquiryUrl = (product, selectedVariant, storeSettings, lang = 'ur') => {
  const waNumber = storeSettings?.whatsappNumber || '923470005578';
  const cleanNumber = waNumber.replace(/[^0-9]/g, '');
  const variant = selectedVariant || product.variants[0];

  let message = '';
  if (lang === 'ur') {
    message = `السلام علیکم حکیم صاحب! 🌿\n\n`;
    message += `میں *${storeSettings.storeNameUrdu}* کی دوا *${product.nameUrdu}* (${variant.weight} - قیمت Rs. ${variant.price}) کے بارے میں طبی رہنمائی اور خریدنے کی تفصیلات معلوم کرنا چاہتا ہوں۔\n\n`;
    message += `برائے مہربانی رہنمائی فرمائیں۔ شکریہ!`;
  } else {
    message = `Assalam-o-Alaikum Hakim Sahib! 🌿\n\n`;
    message += `I am inquiring about *${product.nameEn || product.nameUrdu}* (${variant.weightEn || variant.weight} - Rs. ${variant.price}) from *${storeSettings.storeNameEn}*.\n\n`;
    message += `Please provide further guidance. Thank you!`;
  }

  return `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
};

export const generateGeneralConsultationUrl = (storeSettings, lang = 'ur') => {
  const waNumber = storeSettings?.whatsappNumber || '923470005578';
  const cleanNumber = waNumber.replace(/[^0-9]/g, '');

  const message = lang === 'ur'
    ? `السلام علیکم ورحمۃ اللہ حکیم صاحب! 🌿\n\nمیں *${storeSettings.hakimNameUrdu}* (${storeSettings.dawakhanaNameUrdu}) سے مفت طبی مشورہ و تشخیص حاصل کرنا چاہتا ہوں۔ برائے مہربانی رہنمائی فرمائیں۔`
    : `Assalam-o-Alaikum Hakim Sahib! 🌿\n\nI would like to seek Tibb-e-Pakistani (Qanoon-e-Mufrad Aza) health consultation with ${storeSettings.hakimNameEn} (${storeSettings.dawakhanaNameEn}). Please advise.`;

  return `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
};
